import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { getAuthToken, setAuthToken, setImpersonationToken } from '@/shared/api/client'
import { queryClient } from '@/shared/api/query-client'
import type { AuthPayload, User } from '@/shared/types/domain'

import * as authApi from './auth-api'
import { switchOrganization as switchOrganizationApi } from '@/features/settings/settings-api'

interface LoginInput {
  login: string
  password: string
}

interface RegisterInput {
  name: string
  phone: string
  verification_token: string
  password: string
  password_confirmation: string
  ref?: string
  invitation_token?: string
}

interface AuthContextValue {
  user: User | null
  token: string | null
  isInitializing: boolean
  isAuthenticated: boolean
  login: (input: LoginInput) => Promise<void>
  adoptImpersonation: (payload: AuthPayload) => void
  register: (input: RegisterInput) => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
  switchOrganization: (organizationId: string) => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: PropsWithChildren) {
  const [token, setToken] = useState<string | null>(() => getAuthToken())
  const [user, setUser] = useState<User | null>(null)
  const [isInitializing, setIsInitializing] = useState(true)

  const clearAuth = useCallback(() => {
    setAuthToken(null)
    setToken(null)
    setUser(null)
    // Сбрасываем весь кэш React Query: данные одного пользователя (проекты,
    // диалоги, лиды и т.д.) не должны утечь следующему после смены аккаунта.
    queryClient.clear()
  }, [])

  const bootstrap = useCallback(async () => {
    const existingToken = getAuthToken()

    if (!existingToken) {
      setIsInitializing(false)
      return
    }

    setToken(existingToken)

    try {
      const currentUser = await authApi.me()
      setUser(currentUser)
    } catch {
      clearAuth()
    } finally {
      setIsInitializing(false)
    }
  }, [clearAuth])

  useEffect(() => {
    void bootstrap()
  }, [bootstrap])

  useEffect(() => {
    const onUnauthorized = () => {
      clearAuth()
    }

    window.addEventListener('ai-stone:unauthorized', onUnauthorized)

    return () => window.removeEventListener('ai-stone:unauthorized', onUnauthorized)
  }, [clearAuth])

  const login = useCallback(async (input: LoginInput) => {
    const payload = await authApi.login(input)
    // Чистим кэш предыдущей сессии до установки нового пользователя.
    queryClient.clear()
    setAuthToken(payload.token)
    setToken(payload.token)
    setUser(payload.user)
  }, [])

  // Сессия имперсонации приходит готовой из обмена кода: токен кладём в
  // sessionStorage, а не в localStorage, чтобы не затереть свою сессию.
  const adoptImpersonation = useCallback((payload: AuthPayload) => {
    queryClient.clear()
    setImpersonationToken(payload.token)
    setToken(payload.token)
    setUser(payload.user)
  }, [])

  const register = useCallback(async (input: RegisterInput) => {
    const payload = await authApi.register(input)
    queryClient.clear()
    setAuthToken(payload.token)
    setToken(payload.token)
    setUser(payload.user)
  }, [])

  const logout = useCallback(async () => {
    try {
      if (getAuthToken()) {
        await authApi.logout()
      }
    } catch {
      // no-op: we still clear local auth state below
    } finally {
      clearAuth()
    }
  }, [clearAuth])

  const switchOrganization = useCallback(async (organizationId: string) => {
    await switchOrganizationApi(organizationId)
    // Контекст организации сменился на сервере — данные прошлой организации
    // (проекты, диалоги, биллинг) не должны протечь; перечитываем пользователя.
    const currentUser = await authApi.me()
    // resetQueries, а не clear(): clear() выбрасывает query из кэша, но смонтированные
    // наблюдатели остаются на удалённых объектах со старыми данными. Страница, которая
    // не читает AuthContext (например /billing), не ререндерится от setUser — и показывает
    // цифры прежней организации. reset обнуляет state и перезапрашивает активные queries.
    void queryClient.resetQueries()
    setUser(currentUser)
  }, [])

  const refreshUser = useCallback(async () => {
    const currentUser = await authApi.me()
    setUser(currentUser)
  }, [])

  const value = useMemo(
    () => ({
      user,
      token,
      isInitializing,
      isAuthenticated: Boolean(token && user),
      login,
      adoptImpersonation,
      register,
      logout,
      refreshUser,
      switchOrganization,
    }),
    [
      adoptImpersonation,
      isInitializing,
      login,
      logout,
      refreshUser,
      register,
      switchOrganization,
      token,
      user,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider')
  }

  return context
}
